import React, { useState } from 'react';
import { Mail, Search, User, Info, Power } from 'lucide-react';
import { Email, EmailStatus } from '../types/email';
import { mockEmails, mockSystemRecords } from '../data/mockData';
import { EmailCard } from '../components/EmailCard';
import { EmailDetailView } from '../components/EmailDetailView';
import { ManualLinkModal } from '../components/ManualLinkModal';
export function InboxPage() {
  const [emails, setEmails] = useState<Email[]>(mockEmails);
  const [selectedEmailId, setSelectedEmailId] = useState<string | null>(emails[0]?.id || null);
  const [filterStatus, setFilterStatus] = useState<EmailStatus | 'all'>('all');
  const [showManualLinkModal, setShowManualLinkModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const selectedEmail = emails.find(e => e.id === selectedEmailId);
  const filteredEmails = emails.filter(email => {
    const matchesStatus = filterStatus === 'all' || email.status === filterStatus;
    const matchesSearch = searchQuery === '' || email.from.toLowerCase().includes(searchQuery.toLowerCase()) || email.subject.toLowerCase().includes(searchQuery.toLowerCase()) || email.references.some(ref => ref.number.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesStatus && matchesSearch;
  });
  const statusCounts = {
    processing: emails.filter(e => e.status === 'processing').length,
    'auto-linked': emails.filter(e => e.status === 'auto-linked').length,
    'needs-review': emails.filter(e => e.status === 'needs-review').length,
    'manually-linked': emails.filter(e => e.status === 'manually-linked').length
  };
  const handleManualLink = (recordIds: string[]) => {
    if (!selectedEmail) return;
    const linkedRecords = recordIds.map(id => {
      const record = mockSystemRecords.find(r => r.id === id);
      if (!record) return null;
      return {
        id: record.id,
        type: record.type,
        name: record.name,
        referenceNumber: record.referenceNumber,
        metadata: {
          status: record.status,
          ...record.metadata
        }
      };
    }).filter(Boolean);
    setEmails(emails.map(email => email.id === selectedEmail.id ? {
      ...email,
      status: 'manually-linked' as EmailStatus,
      linkedRecords: [...email.linkedRecords, ...(linkedRecords as any)]
    } : email));
  };
  const handleUnlink = (recordId: string) => {
    if (!selectedEmail) return;
    const updatedLinkedRecords = selectedEmail.linkedRecords.filter(r => r.id !== recordId);
    // If no records left, change status to needs-review
    const newStatus = updatedLinkedRecords.length === 0 ? 'needs-review' : selectedEmail.status;
    setEmails(emails.map(email => email.id === selectedEmail.id ? {
      ...email,
      status: newStatus as EmailStatus,
      linkedRecords: updatedLinkedRecords
    } : email));
  };
  return <div className="h-screen flex flex-col" style={{
    backgroundColor: 'var(--bg-light)'
  }} data-id="element-97">
      {/* Navy Header */}
      <header className="flex items-center justify-between px-6 py-3" style={{
      backgroundColor: 'var(--navy-header)'
    }} data-id="element-98">
        <div className="flex items-center gap-3" data-id="element-99">
          <div className="flex items-center gap-2" data-id="element-100">
            <div className="w-6 h-6 rounded" style={{
            backgroundColor: 'var(--yellow-brand)'
          }} data-id="element-101"></div>
            <span className="text-white font-semibold text-lg" data-id="element-102">ERS</span>
            <span className="text-gray-400 text-sm" data-id="element-103">Demo</span>
          </div>
        </div>

        <div className="flex items-center gap-3" data-id="element-104">
          {/* Status Badges */}
          <div className="flex items-center gap-2 text-xs" data-id="element-105">
            <div className="flex flex-col items-center px-3 py-1 rounded" style={{
            backgroundColor: 'var(--red-status)'
          }} data-id="element-106">
              <span className="text-white font-medium" data-id="element-107">Needs Review</span>
              <span className="text-white font-bold" data-id="element-108">
                {statusCounts['needs-review']}
              </span>
            </div>
            <div className="flex flex-col items-center px-3 py-1 rounded" style={{
            backgroundColor: 'var(--orange-status)'
          }} data-id="element-109">
              <span className="text-white font-medium" data-id="element-110">Processing</span>
              <span className="text-white font-bold" data-id="element-111">
                {statusCounts.processing}
              </span>
            </div>
            <div className="flex flex-col items-center px-3 py-1 rounded" style={{
            backgroundColor: 'var(--gray-status)'
          }} data-id="element-112">
              <span className="text-white font-medium" data-id="element-113">Auto-Linked</span>
              <span className="text-white font-bold" data-id="element-114">
                {statusCounts['auto-linked']}
              </span>
            </div>
            <div className="flex flex-col items-center px-3 py-1 rounded" style={{
            backgroundColor: 'var(--green-status)'
          }} data-id="element-115">
              <span className="text-white font-medium" data-id="element-116">Manual</span>
              <span className="text-white font-bold" data-id="element-117">
                {statusCounts['manually-linked']}
              </span>
            </div>
          </div>

          {/* Search */}
          <div className="relative" data-id="element-118">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-119" />
            <input type="text" placeholder="Search using Name or Reference Number" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10 pr-4 py-2 bg-white border-none rounded text-sm w-80 focus:outline-none focus:ring-2 focus:ring-blue-400" data-id="element-120" />
          </div>

          {/* Icons */}
          <button className="p-2 text-white hover:bg-white/10 rounded" data-id="element-121">
            <User className="w-5 h-5" data-id="element-122" />
          </button>
          <button className="p-2 text-white hover:bg-white/10 rounded" data-id="element-123">
            <Info className="w-5 h-5" data-id="element-124" />
          </button>
        </div>
      </header>

      {/* Teal Section Header */}
      <div className="px-6 py-3" style={{
      backgroundColor: 'var(--teal-section)'
    }} data-id="element-125">
        <h1 className="text-white font-medium text-lg" data-id="element-126">
          Search Correspondences
        </h1>
        <p className="text-white/80 text-sm" data-id="element-127">Email Reference Linking System</p>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden" data-id="element-128">
        {/* Email List */}
        <div className="flex-1 bg-white border-r" style={{
        borderColor: 'var(--border-light)'
      }} data-id="element-129">
          {/* Filter Tabs */}
          <div className="flex items-center gap-1 px-4 py-2 border-b" style={{
          borderColor: 'var(--border-light)',
          backgroundColor: '#fafbfc'
        }} data-id="element-130">
            {(['all', 'processing', 'auto-linked', 'needs-review', 'manually-linked'] as const).map(status => <button key={status} onClick={() => setFilterStatus(status)} className={`px-4 py-2 text-sm font-medium rounded transition-colors ${filterStatus === status ? 'text-white' : 'text-gray-600 hover:bg-gray-100'}`} style={filterStatus === status ? {
            backgroundColor: 'var(--teal-section)'
          } : {}} data-id="element-131">
                {status === 'all' ? 'All' : status.replace('-', ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
              </button>)}
          </div>

          {/* Results Header */}
          <div className="px-4 py-2" style={{
          backgroundColor: 'var(--teal-section)'
        }} data-id="element-132">
            <p className="text-white text-sm" data-id="element-133">
              You have found the following correspondences on the system
            </p>
          </div>

          {/* Email Table */}
          <div className="overflow-y-auto" style={{
          height: 'calc(100vh - 280px)'
        }} data-id="element-134">
            <table className="w-full text-sm" data-id="element-135">
              <thead className="sticky top-0 bg-gray-50 border-b" style={{
              borderColor: 'var(--border-light)'
            }} data-id="element-136">
                <tr data-id="element-137">
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-600" data-id="element-138">
                    Reference No.
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-600" data-id="element-139">
                    Date
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-600" data-id="element-140">
                    Type
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-600" data-id="element-141">
                    Direction
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-600" data-id="element-142">
                    From
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-600" data-id="element-143">
                    Subject
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-600" data-id="element-144"></th>
                </tr>
              </thead>
              <tbody data-id="element-145">
                {filteredEmails.map((email, index) => <EmailCard key={email.id} email={email} isSelected={email.id === selectedEmailId} onClick={() => setSelectedEmailId(email.id)} index={index} data-id="element-146" />)}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 border-t" style={{
          borderColor: 'var(--border-light)'
        }} data-id="element-147">
            <div className="flex gap-2" data-id="element-148">
              <button className="px-3 py-1 text-sm border rounded hover:bg-gray-50" style={{
              borderColor: 'var(--border-light)'
            }} data-id="element-149">
                Copy
              </button>
              <button className="px-3 py-1 text-sm border rounded hover:bg-gray-50" style={{
              borderColor: 'var(--border-light)'
            }} data-id="element-150">
                CSV
              </button>
            </div>
            <div className="flex items-center gap-2" data-id="element-151">
              <button className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded" data-id="element-152">
                First
              </button>
              <button className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded" data-id="element-153">
                Previous
              </button>
              <button className="px-3 py-1 text-sm text-white rounded" style={{
              backgroundColor: 'var(--orange-accent)'
            }} data-id="element-154">
                1
              </button>
              <button className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded" data-id="element-155">
                2
              </button>
              <button className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded" data-id="element-156">
                3
              </button>
              <button className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded" data-id="element-157">
                Next
              </button>
              <button className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded" data-id="element-158">
                Last
              </button>
            </div>
            <span className="text-xs text-gray-500" data-id="element-159">
              Showing 1 to {filteredEmails.length} of {emails.length} entries
            </span>
          </div>
        </div>

        {/* Email Detail Panel */}
        <div className="w-[500px]" style={{
        backgroundColor: 'var(--bg-light)'
      }} data-id="element-160">
          {selectedEmail ? <EmailDetailView email={selectedEmail} onManualLink={() => setShowManualLinkModal(true)} onUnlink={handleUnlink} data-id="element-161" /> : <div className="h-full flex items-center justify-center text-gray-500" data-id="element-162">
              <div className="text-center" data-id="element-163">
                <Mail className="w-12 h-12 mx-auto mb-3 opacity-50" data-id="element-164" />
                <p className="text-sm" data-id="element-165">Select an email to view details</p>
              </div>
            </div>}
        </div>
      </div>

      {showManualLinkModal && selectedEmail && <ManualLinkModal email={selectedEmail} onClose={() => setShowManualLinkModal(false)} onLink={handleManualLink} data-id="element-166" />}
    </div>;
}